<template>
  <div>
    <div class="col-12 p-0">
      <section-header :ressource="ressourceContrat"
                      :isExtended='true'
                      :dialog="dialog"
                      @closeDialog=closeDialog>
      </section-header>

      <v-row class="p-0 m-0">
        <v-col class="col-4 ps-0 pe-4">
          <v-col class="ps-0 pb-6">
            <loader-button class='me-2'
                           type='button'
                           :label="newArticleLabel"
                           :loading="loading"
                           @callback='newArticle'>
            </loader-button>
            <loader-button class='me-2'
                           type='button'
                           :label="newArticleItemLabel"
                           :disabled="!canAddItem"
                           :loading="loading"
                           @callback='newArticleItem'>
            </loader-button>
            <loader-button class='me-2'
                           type='button'
                           :label="moveUpLabel"
                           :disabled="!canMoveUp"
                           :loading="loading"
                           @callback='moveUp'>
            </loader-button>
            <loader-button class='me-2'
                           type='button'
                           :label="moveDownLabel"
                           :disabled="!canMoveDown"
                           :loading="loading"
                           @callback='moveDown'>
            </loader-button>
            <loader-button class='me-2'
                           type='button'
                           :label="removeLabel"
                           :disabled="!canRemove"
                           :loading="loading"
                           @callback='remove'>
            </loader-button>
          </v-col>
          <v-treeview
              v-if="items.length > 0"
              v-model:activated="activeItemIds"
              :opened="[parentNode]"
              :items="items"
              return-object
              activatable
              dense
          >
            <template #title="{ item }">
              <span>
                {{ getHtmlNode(item) }}
              </span>

            </template>
          </v-treeview>
        </v-col>

        <v-col class="col-8 ps-4">
          <edit-field v-if="isTemplate"
                      :value="contratTemplate.name"
                      :editFieldInfo="fieldInfoTemplate"
                      @updated="setTemplateName">
          </edit-field>
          <edit-field v-if="isItem"
                      :value="articleComment"
                      :editFieldInfo="fieldInfoArticleInfo"
                      @updated="setArticleItemTitle">
          </edit-field>
          <edit-field v-if="isArticle"
                      :value="articleTitle"
                      :editFieldInfo="fieldInfoArticleTitle"
                      @updated="setArticleTitle">
          </edit-field>
          <html-field v-if="isItem"
                      :label="htmlLabel"
                      :value="htmlValue"
                      :params="htmlParams"
                      @updated="setHtml">
          </html-field>
          <v-combobox v-if="contratTemplate.useConvention"
                      v-model="selectedConventions"
                      :items="conventions"
                      label="Conventions applicables"
                      multiple>
          </v-combobox>
          <v-combobox v-if="contratTemplate.useConvention"
                      v-model="selectedMetiers"
                      :items="metiers"
                      label="Métiers conventionnels applicables"
                      multiple>
          </v-combobox>
          <v-checkbox v-if="isItem"
                      v-model="notRequired"
                      label="Item non affiché si un paramètre de publication est absent"
                      hide-details>
          </v-checkbox>
        </v-col>
      </v-row>

      <div class="d-flex flex-row-reverse mt-4">
        <loader-button class="me-2"
                       type='button'
                       :label="saveLabel"
                       :disabled="unchanged"
                       :loading="saving"
                       @callback='save'>
        </loader-button>
        <loader-button v-if="isInternal"
                       class="me-2"
                       type='button'
                       :outlined="true"
                       :label="previewLabel"
                       :loading="previewing"
                       @callback='preview'>
        </loader-button>
        <loader-button v-if="isInternal && contratTemplate.clientId > 0"
                       class="me-2"
                       type='button'
                       :outlined="true"
                       :label="configurationLabel"
                       :loading="loading"
                       @callback='openConfiguration'>
        </loader-button>
      </div>
    </div>
    <section-container :section="section"
                       :dialogOpen="dialogOpen"
                       :extended="true"
                       @saved="configurationUpdated"
                       @closeDialog="dialogOpen=false">
    </section-container>
  </div>
</template>

<script lang="ts">
import LoaderButton from '@/framework/base/components/LoaderButton.vue';
import SectionHeader from '@/framework/base/components/SectionHeader.vue';
import ContratArticle, {ContratArticleTypes} from '@/generated/classes/ContratArticle';
import {FwkButtons, RessourceFwkButton} from '@/ressources/globalRessources';
import {isMobile} from '@/style/StyleHelpers';
import {TreeNode} from '@/framework/base/components/TreeNode';
import {ObjTypes} from '@/generated/classes/ObjTypes';
import ArticleItem, {ArticleItemTypes} from '@/generated/classes/ArticleItem';
import TreeView from '@/framework/base/components/TreeView.vue';
import EditField from '@/framework/base/components/EditField.vue';
import HtmlField from '@/framework/base/components/HtmlField.vue';
import {EditFieldType, FieldInfo} from '@/framework/base/components/FieldInfo';
import {MapRessource} from '@/framework/base/helpers/Ressources';
import HtmlAttrib, {HtmlParam} from '@/generated/classes/HtmlAttrib'
import ContratTemplate, {ArticleTemplate} from '@/generated/classes/ContratTemplate';
import Graph, {ConventionMetiers} from '@/generated/classes/Graph';
import {SELECT_TEXT} from '@/framework/base/components/SelectField.vue';
import DialogHubManager from "@/framework/dialog/helpers/DialogHubManager";
import SectionContainer from '@/framework/base/components/SectionContainer.vue';
import Section from '@/framework/base/classes/Section';
import SectionPreviewParam from '@/custom/sections/SectionPreviewParam.vue';
import SectionContratTemplateConfigExtend from '@/extend/sections/SectionContratTemplateConfigExtend.vue';
import {GenSection, RessourceGenSection} from '@/generated/ressources/generatedRessources';
import {storeContratTemplate, storeDocument} from '@/generated/stores';
import {defineComponent} from "vue";

export default defineComponent({
  components: {
    HtmlField,
    EditField,
    LoaderButton,
    SectionHeader,
    SectionContainer
  },
  props: {
    hideTitle: {
      type: Boolean
    },
    contratTemplateId: {
      type: Number,
      required: true
    },
    contratId: {
      type: Number,
      required: true
    },
    dialog: {
      type: Boolean
    }
  },
  created() {
    storeDocument.registerDocument();
    if (this.contratId) {
      this.isInternal = false;
    }
    DialogHubManager.waitAnswer('metaDefined', this.metaDefined);
    Graph.listConventionMetiers().then((res) => {
      this.conventionMetiers = res;
    });
    ContratTemplate.load(this.contratTemplateId).then((res) => {
      this.contratTemplate = res;
      this.contratTemplate.contratId = this.contratId;
      this.treeNodes();
    });
    HtmlAttrib.listParam('Contrats').then((res) => {
      this.htmlAttrib.publiParams = res;
    })
  },
  data() {
    const newItem = null as ArticleItem | null;
    const newItemParentNode = undefined as TreeNode | undefined;
    const conventionMetiers: ConventionMetiers[] = [];
    const selectedNode = null as TreeNode | null;
    const parentNode = null as TreeNode | null;
    const articlesSelected: ContratArticle[] = [];
    const selectedArticle: ContratArticle = new ContratArticle;
    return {
      isMobile: isMobile(),
      isInternal: true,
      selectedArticle,
      articlesSelected,
      dialogOpen: false,
      dialogType: '',
      articlesUpdated: false,
      updated: false,
      loading: false,
      saving: false,
      previewing: false,
      selectedNode,
      parentNode,
      htmlAttrib: new HtmlAttrib(),
      conventionMetiers,
      contratTemplate: new ContratTemplate(),
      refContratTemplate: new ContratTemplate(),
      configurationTemplate: new ContratTemplate(),
      newItemParentNode,
      newItem,
      cloned: false,
      openNodes: [],
      items: [],
    };
  },
  computed: {
    activeItemIds: {
      get(): TreeNode[] {
        return [this.selectedNode];
      },
      set(value: TreeNode[]) {
        this.setActiveNode(value[0]);
      }
    },
    ressourceContrat() {
      const ressource = new MapRessource();
      ressource.label = this.contratTemplate.name;
      return ressource;
    },
    saveLabel() {
      return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
    },
    previewLabel() {
      return 'Enregistrer et visualiser';
    },
    configurationLabel() {
      return RessourceGenSection(GenSection.CONTRAT_TEMPLATE_CONFIG).label;
    },
    newArticleLabel() {
      return RessourceFwkButton(FwkButtons.NEW_ARTICLE).label;
    },
    newArticleItemLabel() {
      return RessourceFwkButton(FwkButtons.NEW_ARTICLE_ITEM).label;
    },
    moveUpLabel() {
      return RessourceFwkButton(FwkButtons.MOVE_UP).label;
    },
    moveDownLabel() {
      return RessourceFwkButton(FwkButtons.MOVE_DOWN).label;
    },
    removeLabel() {
      return RessourceFwkButton(FwkButtons.SUPPRIMER).label;
    },
    canMoveUp(): boolean {
      let res = false;
      if (this.selectedNode && this.newItem == null) {
        const countRange = this.getCountRange(this.selectedNode);
        if (countRange.length == 2) {
          res = countRange[1] > 0;
        }
      }
      return res;
    },
    canMoveDown(): boolean {
      let res = false;
      if (this.selectedNode && this.newItem == null) {
        const countRange = this.getCountRange(this.selectedNode);
        if (countRange.length == 2) {
          res = countRange[1] >= 0 && countRange[1] < countRange[0] - 1;
        }
      }
      return res;
    },
    canRemove(): boolean {
      let res = false;
      if (this.selectedNode && this.newItem == null) {
        res = this.selectedNode.objType == ObjTypes.ARTICLE_ITEM ||
            this.selectedNode.objType == ObjTypes.CONTRAT_ARTICLE;
      }
      return res;
    },
    canAddItem(): boolean {
      let res = false;
      if (this.selectedNode?.objType != ObjTypes.CONTRAT) {
        res = true;
      }
      return res;
    },
    selectedConventions: {
      get(): string[] {
        let res: string[] = [];
        if (this.selectedNode) {
          if (this.selectedNode.objType == ObjTypes.CONTRAT) {
            res = this.getConventions(this.contratTemplate.conventionIds);
          } else if (this.selectedNode.objType == ObjTypes.CONTRAT_ARTICLE) {
            const article = this.selectedNode.obj as ContratArticle;
            res = this.getConventions(article.conventionIds);
          } else if (this.selectedNode.objType == ObjTypes.ARTICLE_ITEM) {
            const item = this.selectedNode.obj as ArticleItem;
            res = this.getConventions(item.conventionIds);
          }
        }
        return res;
      },
      set(value: string[]) {
        if (this.selectedNode) {
          if (this.selectedNode.objType == ObjTypes.CONTRAT) {
            this.contratTemplate.conventionIds = this.setConventions(value);
          } else if (this.selectedNode.objType == ObjTypes.CONTRAT_ARTICLE) {
            const article = this.selectedNode.obj as ContratArticle;
            article.conventionIds = this.setConventions(value);
          } else if (this.selectedNode.objType == ObjTypes.ARTICLE_ITEM) {
            const item = this.selectedNode.obj as ArticleItem;
            item.conventionIds = this.setConventions(value);
          }
        }
        this.update()
      }
    },
    conventions(): string[] {
      const res: string[] = [];
      this.conventionMetiers.forEach((cm) => {
        res.push(cm.convention);
      })
      return res.sort((n1, n2) => n1 > n2 ? 1 : -1);
    },
    selectedMetiers: {
      get(): string[] {
        let res: string[] = [];
        if (this.selectedNode) {
          if (this.selectedNode.objType == ObjTypes.CONTRAT) {
            res = this.getMetiers(this.contratTemplate.metierIds);
          } else if (this.selectedNode.objType == ObjTypes.CONTRAT_ARTICLE) {
            const article = this.selectedNode.obj as ContratArticle;
            res = this.getMetiers(article.metierIds);
          } else if (this.selectedNode.objType == ObjTypes.ARTICLE_ITEM) {
            const item = this.selectedNode.obj as ArticleItem;
            res = this.getMetiers(item.metierIds);
          }
        }
        return res;
      },
      set(value: string[]) {
        if (this.selectedNode) {
          if (this.selectedNode.objType == ObjTypes.CONTRAT) {
            this.contratTemplate.metierIds = this.setMetiers(value);
          } else if (this.selectedNode.objType == ObjTypes.CONTRAT_ARTICLE) {
            const article = this.selectedNode.obj as ContratArticle;
            article.metierIds = this.setMetiers(value);
          } else if (this.selectedNode.objType == ObjTypes.ARTICLE_ITEM) {
            const item = this.selectedNode.obj as ArticleItem;
            item.metierIds = this.setMetiers(value);
          }
        }
        this.update()
      }
    },
    metiers(): string[] {
      const res: string[] = [];
      this.conventionMetiers.forEach((cms) => {
        if ((this.selectedConventions.length == 1 && this.selectedConventions.includes(SELECT_TEXT)) ||
            this.selectedConventions.includes(cms.convention)) {
          cms.metiers.forEach((m) => {
            if (!res.includes(m.metier)) {
              res.push(m.metier);
            }
          })
        }
      })
      return res.sort((n1, n2) => n1 > n2 ? 1 : -1);
    },
    isArticle(): boolean {
      return this.selectedNode != null && (this.selectedNode.objType == ObjTypes.CONTRAT_ARTICLE || this.selectedNode.objType == ObjTypes.CONTRAT)
    },
    articleTitle(): string {
      let res = 'Not found';
      if (this.selectedNode) {
        const article = this.selectedNode.obj as ContratArticle;
        if (article) {
          res = article.titre;
        }
      }
      return res;
    },
    articleComment(): string {
      let res = 'Not found';
      if (this.selectedNode) {
        const item = this.selectedNode.obj as ArticleItem;
        if (item) {
          res = item.commentaire;
        }
      }
      return res;
    },
    fieldInfoTemplate(): FieldInfo {
      const ressourceName = new MapRessource();
      ressourceName.label = 'Nom';
      const type = EditFieldType.STRING
      const precision = 2;
      const format = null;
      const mask = null;
      const live = null;
      const focus = null;
      return new FieldInfo(ressourceName, type, precision, mask, format, focus, live);
    },
    fieldInfoArticleTitle(): FieldInfo {
      const ressourceTitre = new MapRessource();
      ressourceTitre.label = 'Titre';
      const type = EditFieldType.STRING
      const precision = 2;
      const format = null;
      const mask = null;
      const live = null;
      const focus = null;
      return new FieldInfo(ressourceTitre, type, precision, mask, format, focus, live);
    },
    fieldInfoArticleInfo(): FieldInfo {
      const ressourceInfo = new MapRessource();
      ressourceInfo.label = 'Information';
      const type = EditFieldType.STRING
      const precision = 2;
      const format = null;
      const mask = null;
      const live = null;
      const focus = null;
      return new FieldInfo(ressourceInfo, type, precision, mask, format, focus, live);
    },
    isTemplate(): boolean {
      return this.selectedNode != null && (this.selectedNode.objType == ObjTypes.CONTRAT);
    },
    isItem(): boolean {
      return this.selectedNode != null && (this.selectedNode.objType == ObjTypes.ARTICLE_ITEM);
    },
    htmlLabel(): string {
      return 'Texte HTML de l\'item de l\'article';
    },
    htmlValue(): string {
      return this.htmlAttrib.html;
    },
    htmlParams(): HtmlParam[] {
      return this.htmlAttrib.publiParams;
    },
    notRequired: {
      get(): boolean {
        let res = true;
        if (this.selectedNode) {
          const item = this.selectedNode.obj as ArticleItem;
          res = !item.requis;
        }
        return res;
      },
      set(value: boolean) {
        if (this.selectedNode) {
          const item = this.selectedNode.obj as ArticleItem;
          item.requis = !value;
          this.update();
        }
      }
    },
    unchanged(): boolean {
      return !this.updated;
    },
    section(): Section {
      const section = new Section();
      if (this.dialogType == 'configuration') {
        section.component = SectionContratTemplateConfigExtend;
        section.type = 'contratTemplate';
        section.data = this.configurationTemplate;
        section.showButton = true;
      } else {
        section.component = SectionPreviewParam;
        section.type = 'contratTemplate';
        section.data = this.contratTemplate;
      }
      return section;
    }
  },

  methods: {
    treeNodes(): TreeNode[] {
      const res: TreeNode[] = [];
      let id = 0;
      let numero = 1;
      const loaded = this.contratTemplate.articles.find(a => a.articleType == ContratArticleTypes.EMPLOYEUR);
      if (loaded) {
        const node = new TreeNode();
        node.objType = ObjTypes.CONTRAT;
        const article = new ContratArticle();
        article.titre = this.contratTemplate.titre;
        node.obj = article;
        node.id = id++;
        node.canDelete = false;
        node.children = null;
        res.push(node);
        this.contratTemplate.articles.forEach((articleTemplate) => {
          const node = new TreeNode();
          if (articleTemplate.articleType == ContratArticleTypes.ARTICLE) {
            articleTemplate.article.numero = numero++;
            node.objType = ObjTypes.CONTRAT_ARTICLE;
          } else if (articleTemplate.articleType == ContratArticleTypes.EMPLOYEUR) {
            node.objType = ObjTypes.ETABLISSEMENT;
          } else if (articleTemplate.articleType == ContratArticleTypes.SALARIE) {
            node.objType = ObjTypes.SALARIE;
          }
          node.obj = articleTemplate.article;
          node.id = id++;
          node.canDelete = articleTemplate.article.canDelete && articleTemplate.article.id > 0;
          res.push(node);
          articleTemplate.items.forEach((item) => {
            const node = new TreeNode();
            node.obj = item;
            node.objType = ObjTypes.ARTICLE_ITEM;
            node.id = id++;
            node.canDelete = item.canDelete && item.id > 0;
            res.forEach((parent) => {
              const article = parent.obj as ContratArticle;
              if (article.id == item.articleId &&
                  (parent.objType == ObjTypes.CONTRAT_ARTICLE ||
                      parent.objType == ObjTypes.ETABLISSEMENT ||
                      parent.objType == ObjTypes.SALARIE)) {
                node.parentId = parent.id;
                node.children = null;
                parent.children.push(node);
              }
            })
          })
        })
        if (!this.cloned) {
          this.cloneContratTemplate();
        }
      }
      if (this.items != res) {
        this.items = res;
      }

      if (this.newItemParentNode && this.newItem) {
        const node = res.find(a => a.id == this.newItemParentNode?.id);
        if (node) {
          const selectItem = node.children.find(a => a.obj == this.newItem)
          if (selectItem) {
            this.setSelectedNode(selectItem);
          }
        }
      }

      return this.items;
    },
    setSelectedNode(selectItem: TreeNode) {
      this.setActiveNode(selectItem);
      this.newItem = null;
    },
    findTreeNode(nodes: TreeNode[], id: number): TreeNode | null {
      for (const node of nodes) {
        if (node.id == id) {
          return node;
        }
        if (node.children) {
          const child = this.findTreeNode(node.children, id);
          if (child) {
            return child;
          }
        }
      }
      return null;
    },
    cloneContratTemplate() {
      this.refContratTemplate = this.contratTemplate.cloneContratTemplate();
      this.cloned = true;
    },
    getHtmlNode(node: TreeNode): string {
      if (node.objType == ObjTypes.CONTRAT_ARTICLE) {
        const article = node.obj as ContratArticle;
        return 'Article ' + article.numero + ' : ' + article.titre;
      } else if (node.objType == ObjTypes.ETABLISSEMENT) {
        return 'Employeur';
      } else if (node.objType == ObjTypes.SALARIE) {
        return 'Salarié';
      } else if (node.objType == ObjTypes.ARTICLE_ITEM) {
        const item = node.obj as ArticleItem;
        return item.commentaire;
      } else {
        return 'Contrat';
      }
    },
    setActiveNode(node: TreeNode): void {
      this.selectedNode = node;
      if (this.selectedNode && this.selectedNode.parentId != null) {
        const parent = this.items.find(a => a.id == this.selectedNode.parentId);
        if (parent) {
          this.parentNode = parent;
        }
      } else {
        this.parentNode = [];
      }
      this.update();
      if (node.objType == ObjTypes.ARTICLE_ITEM) {
        const item = node.obj as ArticleItem;
        let refItem = new ArticleItem();
        this.refContratTemplate.articles.forEach((templateArticle) => {
          templateArticle.items.forEach((rItem) => {
            if (rItem.id == item.id) {
              refItem = rItem;
            }
          });
        });
        if (refItem) {
          if (refItem.texte == null) {
            HtmlAttrib.get(item.texteHtmlId).then((res) => {
              this.htmlAttrib = res;
              item.texte = res.html;
              refItem.texte = res.html;
            });
          } else {
            this.htmlAttrib.html = item.texte;
          }
        }
      }
    },
    closeDialog() {
      this.$emit('closeDialog');
    },
    moveUp(): void {
      if (this.selectedNode) {
        const selectedNode = this.selectedNode;
        const countRange = this.getCountRange(selectedNode);
        if (this.selectedNode.objType == ObjTypes.CONTRAT_ARTICLE) {
          const range = countRange[1] + 2;
          const articles: ArticleTemplate[] = [];
          this.contratTemplate.articles.forEach((article) => {
            articles.push(article)
          });
          const previous = articles[range - 1];
          articles[range - 1] = articles[range];
          articles[range] = previous;
          this.contratTemplate.articles = articles;
          this.treeNodes();
          const newSelected = this.items.find(a => a.obj == selectedNode.obj);
          if (newSelected) {
            this.setActiveNode(newSelected);
          }
        } else if (this.selectedNode.objType == ObjTypes.ARTICLE_ITEM) {
          const range = countRange[1];
          const parent = this.items.find(a => a.id == selectedNode.parentId)
          if (parent) {
            const article = parent.obj as ContratArticle;
            const articletemplate = this.contratTemplate.articles.find(a => a.articleId == article.id);
            if (articletemplate) {
              const items = articletemplate.items
              const previous = items[range - 1];
              items[range - 1] = items[range];
              items[range] = previous;
              const itemIds = articletemplate.itemIds
              const previousId = itemIds[range - 1];
              itemIds[range - 1] = itemIds[range];
              itemIds[range] = previousId;
              itemIds.forEach((id, index) => {
                if (id == undefined) {
                  itemIds[index] = 0;
                }
              });
              const articles: ArticleTemplate[] = [];
              this.contratTemplate.articles.forEach((article) => {
                articles.push(article)
              });
              this.contratTemplate.articles = articles;
              this.treeNodes();
              const parentNode = this.items.findIndex(a => a.id == selectedNode.parentId);
              if (parentNode) {
                const newSelected = this.items[parentNode].children.find(a => a.obj == selectedNode.obj);
                if (newSelected) {
                  this.setActiveNode(newSelected);
                }
              }
            }
          }
        }
      }

      this.update();
    },
    moveDown(): void {
      if (this.selectedNode) {
        const selectedNode = this.selectedNode;
        const countRange = this.getCountRange(selectedNode);
        if (this.selectedNode.objType == ObjTypes.CONTRAT_ARTICLE) {
          const range = countRange[1] + 2;
          const articles: ArticleTemplate[] = [];
          this.contratTemplate.articles.forEach((article) => {
            articles.push(article)
          });
          const next = articles[range + 1];
          articles[range + 1] = articles[range];
          articles[range] = next;
          this.contratTemplate.articles = articles;
          this.treeNodes();
          const newSelected = this.items.find(a => a.obj == selectedNode.obj);
          if (newSelected) {
            this.setActiveNode(newSelected);
          }
        } else if (this.selectedNode.objType == ObjTypes.ARTICLE_ITEM) {
          const range = countRange[1];
          const parent = this.items.find(a => a.id == selectedNode.parentId)
          if (parent) {
            const article = parent.obj as ContratArticle;
            const articletemplate = this.contratTemplate.articles.find(a => a.articleId == article.id);
            if (articletemplate) {
              const items = articletemplate.items
              const next = items[range + 1];
              items[range + 1] = items[range];
              items[range] = next;
              const itemIds = articletemplate.itemIds
              const nextId = itemIds[range + 1];
              itemIds[range + 1] = itemIds[range];
              itemIds[range] = nextId;
              itemIds.forEach((id, index) => {
                if (id == undefined) {
                  itemIds[index] = 0;
                }
              });
              const articles: ArticleTemplate[] = [];
              this.contratTemplate.articles.forEach((article) => {
                articles.push(article)
              });
              this.contratTemplate.articles = articles;
              this.treeNodes();
              const parentNode = this.items.findIndex(a => a.id == selectedNode.parentId);
              if (parentNode) {
                const newSelected = this.items[parentNode].children.find(a => a.obj == selectedNode.obj);
                if (newSelected) {
                  this.setActiveNode(newSelected);
                }
              }
            }
          }
        }
      }
      this.update();
    },
    remove() {
      const selectedNode = this.selectedNode;
      if (selectedNode) {
        if (selectedNode.objType == ObjTypes.ARTICLE_ITEM) {
          const parentNode = this.items.find(a => a.id == selectedNode.parentId);
          if (parentNode) {
            const article = parentNode.obj as ContratArticle;
            const parentArticle = this.contratTemplate.articles.find(a => a.articleId == article.id);
            if (parentArticle) {
              const articleItem = selectedNode.obj as ArticleItem;
              const find = parentArticle.items.find(a => a.id == articleItem.id);
              if (find) {
                const index = parentArticle.items.indexOf(find);
                parentArticle.items.splice(index, 1);
                this.treeNodes();
              }
            }
          }
        } else if (selectedNode.objType == ObjTypes.CONTRAT_ARTICLE) {
          const article = selectedNode.obj as ContratArticle;
          const find = this.contratTemplate.articles.find(a => a.articleId == article.id);
          if (find) {
            const index = this.contratTemplate.articles.indexOf(find);
            this.contratTemplate.articles.splice(index, 1);
            this.treeNodes();
          }
        }
      }
    },
    newArticle(): void {
      const article = new ArticleTemplate();
      article.articleType = ContratArticleTypes.ARTICLE;
      article.article.titre = this.newArticleLabel;
      this.contratTemplate.articles.push(article);
      this.treeNodes();
      const parent = this.items.find(a => JSON.stringify(a.obj) == JSON.stringify(article.article));
      if (parent) {
        this.selectedNode = parent;
      }
    },
    newArticleItem(): void {
      if (this.selectedNode) {
        const selectedNode = this.selectedNode;
        let article = selectedNode.obj as ContratArticle;
        let articletemplate = new ArticleTemplate();
        let type = ArticleItemTypes.ITEM;

        if (this.selectedNode.objType == ObjTypes.CONTRAT_ARTICLE || this.selectedNode.objType == ObjTypes.ETABLISSEMENT || this.selectedNode.objType == ObjTypes.SALARIE) {
          const template = this.contratTemplate.articles.find(a => a.article.id == article.id);
          if (template) {
            this.newItemParentNode = selectedNode;
            articletemplate = template;
            if (this.selectedNode.objType == ObjTypes.ETABLISSEMENT) {
              type = ArticleItemTypes.ETABLISSEMENT;
            } else if (this.selectedNode.objType == ObjTypes.SALARIE) {
              type = ArticleItemTypes.SALARIE;
            }
          }
        } else if (this.selectedNode.objType == ObjTypes.ARTICLE_ITEM) {
          const parent = this.items.find(a => a.id == selectedNode.parentId);
          if (parent) {
            this.newItemParentNode = parent;
            if (parent.objType == ObjTypes.ETABLISSEMENT) {
              type = ArticleItemTypes.ETABLISSEMENT;
            } else if (parent.objType == ObjTypes.SALARIE) {
              type = ArticleItemTypes.SALARIE;
            }
            article = parent.obj as ContratArticle;
            const template = this.contratTemplate.articles.find(a => a.article.id == article.id);
            if (template) {
              articletemplate = template;
            }
          }
        }
        if (articletemplate) {
          const item = new ArticleItem();
          item.articleId = article.id;
          item.type = type;
          item.commentaire = this.newArticleItemLabel;
          item.requis = true;
          articletemplate.items.push(item);
          this.newItem = item;
          this.treeNodes();
        }
      }
    },
    getCountRange(node: TreeNode): number[] {
      const res: number[] = []
      if (node.objType == ObjTypes.CONTRAT_ARTICLE) {
        const article = node.obj as ContratArticle;
        const template = this.contratTemplate.articles.find(a => a.article.id == article.id);
        if (template) {
          res.push(this.contratTemplate.articles.length - 2);
          res.push(this.contratTemplate.articles.indexOf(template) - 2);
        }
      } else if (node.objType == ObjTypes.ARTICLE_ITEM) {
        const item = node.obj as ArticleItem;
        const parent = this.items.find(a => a.id == node.parentId)
        if (parent) {
          const article = parent.obj as ContratArticle;
          const articleTemplate = this.contratTemplate.articles.find(a => a.articleId == article.id);
          if (articleTemplate) {
            res.push(articleTemplate.items.length);
            res.push(articleTemplate.items.indexOf(item));
          }
        }
      }
      return res;
    },
    getConventions(values: number[]): string[] {
      const res: string[] = [];
      this.conventionMetiers.forEach((cm) => {
        if (values.includes(cm.conventionId)) {
          res.push(cm.convention);
        }
      });
      if (res.length == 0) {
        res.push(SELECT_TEXT)
      }
      return res;
    },
    setConventions(values: string[]): number[] {
      const res: number[] = [];
      this.conventionMetiers.forEach((cm) => {
        if (values.includes(cm.convention)) {
          res.push(cm.conventionId);
        }
      });
      return res;
    },
    getMetiers(values: number[]): string[] {
      const res: string[] = [];
      this.conventionMetiers.forEach((cms) => {
        cms.metiers.forEach((m) => {
          if (values.includes(m.metierId) && !res.includes(m.metier)) {
            res.push(m.metier);
          }
        })
      });
      if (res.length == 0) {
        res.push(SELECT_TEXT)
      }
      return res;
    },
    setMetiers(values: string[]): number[] {
      const res: number[] = [];
      this.conventionMetiers.forEach((cms) => {
        cms.metiers.forEach((m) => {
          if (values.includes(m.metier) && !res.includes(m.metierId)) {
            res.push(m.metierId);
          }
        });
      });
      return res;
    },
    setArticleTitle(value: string) {
      if (this.selectedNode) {
        if (this.selectedNode.objType == ObjTypes.CONTRAT) {
          this.contratTemplate.titre = value;
        } else {
          const article = this.selectedNode.obj as ContratArticle;
          article.titre = value;
        }
        this.update();
      }
    },
    setTemplateName(value: string) {
      if (this.selectedNode) {
        this.contratTemplate.name = value;
        this.update();
      }
    },
    setArticleItemTitle(value: string) {
      if (this.selectedNode) {
        const item = this.selectedNode.obj as ArticleItem;
        item.commentaire = value;
        this.update();
      }
    },
    setHtml(value: string) {
      if (this.selectedNode) {
        const item = this.selectedNode.obj as ArticleItem;
        item.texte = value;
        this.update();
      }
    },
    update(): void {
      this.updated = !this.refContratTemplate.compareContratTemplate(this.contratTemplate);
    },
    applySavedTemplate(savedTemplate: ContratTemplate, selectedNodeId?: number): void {
      this.contratTemplate.fromContratTemplate(savedTemplate);
      this.refContratTemplate = this.contratTemplate.cloneContratTemplate();
      this.updated = false;
      this.cloned = true;
      this.newItemParentNode = undefined;
      this.newItem = null;
      this.items = [];
      this.treeNodes();

      if (selectedNodeId != undefined) {
        const selectedNode = this.findTreeNode(this.items, selectedNodeId);
        if (selectedNode) {
          this.setSelectedNode(selectedNode);
        }
      }
    },
    metaDefined(_obj: unknown) {
      const selectedNodeId = this.selectedNode?.id;
      this.saving = true;
      ContratTemplate.load(this.contratTemplate.id)
          .then((savedTemplate) => this.applySavedTemplate(savedTemplate, selectedNodeId))
          .finally(() => {
            this.saving = false;
          });
    },
    preview(): void {
      const selectedNodeId = this.selectedNode?.id;
      this.previewing = true;
      ContratTemplate.save(this.contratTemplate)
          .then((savedTemplate) => {
            this.applySavedTemplate(savedTemplate, selectedNodeId);

            if (this.contratTemplate.useConvention) {
              this.dialogType = 'preview';
              this.dialogOpen = true;
              return;
            }

            this.contratTemplate.conventionId = 367 // 3090;
            this.contratTemplate.metierId = 34 // CHANTEUR;
            return ContratTemplate.preview(this.contratTemplate);
          })
          .finally(() => {
            this.previewing = false;
          });
    },
    openConfiguration(): void {
      const configurationTemplate = storeContratTemplate.getContratTemplate(this.contratTemplate.id);
      configurationTemplate.fromContratTemplate(this.contratTemplate);
      this.configurationTemplate = configurationTemplate;
      this.dialogType = 'configuration';
      this.dialogOpen = true;
    },
    configurationUpdated(value: ContratTemplate): void {
      if (this.dialogType == 'configuration' && value) {
        this.contratTemplate.name = value.name;
        this.contratTemplate.titre = value.titre;
        this.contratTemplate.clientId = value.clientId;
        this.contratTemplate.etablissementId = value.etablissementId;
        this.contratTemplate.useConvention = value.useConvention;
        this.contratTemplate.active = value.active;
        this.refContratTemplate.name = value.name;
        this.refContratTemplate.titre = value.titre;
        this.refContratTemplate.clientId = value.clientId;
        this.refContratTemplate.etablissementId = value.etablissementId;
        this.refContratTemplate.useConvention = value.useConvention;
        this.refContratTemplate.active = value.active;
        this.dialogOpen = false;
      }
    },
    save(): void {
      const selectedNodeId = this.selectedNode?.id;
      this.saving = true;
      ContratTemplate.save(this.contratTemplate)
          .then((savedTemplate) => this.applySavedTemplate(savedTemplate, selectedNodeId))
          .finally(() => {
            this.saving = false;
          });
    }
  }
})

</script>
